// Импортируем chartIdCounter для стабильной генерации ID
import { 
  executeChartScripts, 
  extractChartConfig, 
  createChartHtml,
  renderChart,
  showChartError,
  initCharts
} from './chartRenderer';
import { setSafeElementIdentifiers } from './safeDomUtils';
import { extractPartialData } from './partialDataExtractor';

// Strip HTML tags from table cell values (LLM sometimes adds <strong> etc.)
function stripHtmlTags(value: string): string {
  return value.replace(/<[^>]*>/g, '');
}

// Создаем локальную копию счетчика для ID
let chartIdCounter = 0;

// Re-export chart-related functions for backward compatibility
export { executeChartScripts, initCharts, renderChart, showChartError };

// Проверка и загрузка Chart.js
const ensureChartJsLoaded = (callback: () => void) => {
  console.log('[REPORT_RENDERER] ensureChartJsLoaded - проверка наличия Chart.js');
  
  // Проверяем наличие Chart.js
  if (window.Chart) {
    console.log('[REPORT_RENDERER] ensureChartJsLoaded - Chart.js уже загружен');
    callback();
    return;
  }
  
  console.log('[REPORT_RENDERER] ensureChartJsLoaded - Chart.js не найден, загружаем динамически');
  
  // Загружаем Chart.js динамически
  const chartScript = document.createElement('script');
  chartScript.src = 'https://cdn.jsdelivr.net/npm/chart.js';
  chartScript.onload = () => {
    console.log('[REPORT_RENDERER] ensureChartJsLoaded - Chart.js успешно загружен');
    callback();
  };
  chartScript.onerror = (error) => {
    console.error('[REPORT_RENDERER] ensureChartJsLoaded - ошибка при загрузке Chart.js:', error);
    // Все равно вызываем callback, чтобы продолжить рендеринг
    callback();
  };
  
  document.head.appendChild(chartScript);
};

// Рендеринг полного отчета
export const renderReport = (reportData: any, container: HTMLElement) => {
  console.log('[REPORT_RENDERER] renderReport - начало выполнения');
  console.log('[REPORT_RENDERER] renderReport - данные отчета:', reportData);
  console.log('[REPORT_RENDERER] renderReport - контейнер:', container);
  
  if (!reportData || !container) {
    console.error('[REPORT_RENDERER] renderReport - ошибка: Invalid report data or container');
    return;
  }
  
  // Проверяем наличие графиков в отчете
  const hasCharts = (reportData.blocks && reportData.blocks.some((block: any) => block.type === 'visualization' && block.chart_html)) ||
                    (reportData.visualizations && reportData.visualizations.some((viz: any) => viz.chart_html)) ||
                    (reportData.reports && reportData.reports.some((report: any) =>
                      report.actions && report.actions.visualizations &&
                      report.actions.visualizations.some((viz: any) => viz.chart_html)));
  
  console.log(`[REPORT_RENDERER] renderReport - отчет ${hasCharts ? 'содержит' : 'не содержит'} графики`);
  
  // Если в отчете есть графики, убедимся, что Chart.js загружен
  if (hasCharts) {
    ensureChartJsLoaded(() => {
      renderReportContent(reportData, container);
    });
  } else {
    // Если графиков нет, просто рендерим отчет
    renderReportContent(reportData, container);
  }
};

// Рендеринг содержимого отчета
const renderReportContent = (reportData: any, container: HTMLElement) => {
  console.log('[REPORT_RENDERER] renderReportContent - начало выполнения');

  // Проверяем формат данных и обрабатываем соответственно
  if (reportData.blocks && Array.isArray(reportData.blocks)) {
    console.log('[REPORT_RENDERER] renderReport - обнаружен формат blocks, количество блоков:', reportData.blocks.length);
    renderBlocks(reportData.blocks, container);
  } else if (reportData.analytical_note && reportData.visualizations) {
    console.log('[REPORT_RENDERER] renderReport - обнаружен новый формат с analytical_note и visualizations');
    // Новый формат с analytical_note и visualizations на верхнем уровне
    renderAnalyticalNote(reportData.analytical_note, container);
    renderVisualizations(reportData.visualizations, container);
  } else {
    console.log('[REPORT_RENDERER] renderReport - обнаружен старый формат с summary и reports');
    // Старый формат с summary и reports
    // Render summary section first
    if (reportData.summary) {
      console.log('[REPORT_RENDERER] renderReport - рендеринг summary');
      renderSummarySection(reportData.summary, container);
    }

    // Render key findings
    if (reportData.summary && reportData.summary.key_findings) {
      console.log('[REPORT_RENDERER] renderReport - рендеринг key_findings');
      renderKeyFindings(reportData.summary.key_findings, container);
    }

    // Render reports sections
    if (reportData.reports && reportData.reports.length > 0) {
      console.log('[REPORT_RENDERER] renderReport - рендеринг reports, количество:', reportData.reports.length);
      renderReportSections(reportData.reports, container);
    }

    // Render recommendations
    if (reportData.summary && reportData.summary.recommendations) {
      console.log('[REPORT_RENDERER] renderReport - рендеринг recommendations');
      renderRecommendations(reportData.summary.recommendations, container);
    }
  }
  
  // Выполняем скрипты для инициализации графиков
  // Это необходимо, так как innerHTML не выполняет скрипты
  console.log('[REPORT_RENDERER] renderReport - выполнение скриптов для инициализации графиков');
  executeChartScripts(container);
  
  console.log('[REPORT_RENDERER] renderReport - завершение выполнения');
};

// Глобальное хранилище для отслеживания уже отрендеренного текста и блоков
// Используем WeakMap для автоматической очистки памяти при удалении контейнера
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderedTextStore = new Map<string, string>();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderedBlocksStore = new Map<string, HTMLElement[]>();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderedContentStore = new Map<string, string>();
// Хранилище для незаконченных HTML-элементов
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const incompleteHtmlStore = new Map<string, string>();

// Функция для проверки, является ли HTML-элемент законченным
// Упрощенная версия, которая всегда возвращает true, так как мы предполагаем,
// что с сервера приходят уже готовые куски HTML
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isCompleteHtmlElement = (html: string): boolean => {
  return true;
};

// Рендеринг блочного формата (blocks)
const renderBlocks = (blocks: any[], container: HTMLElement) => {
  console.log('[REPORT_RENDERER] renderBlocks - начало выполнения, блоков:', blocks.length);

  blocks.forEach((block: any) => {
    if (block.type === 'heading') {
      const level = Math.min(Math.max(block.level || 2, 1), 6);
      const heading = document.createElement(`h${level}`);
      heading.textContent = block.text || '';
      heading.className = 'block-heading';
      container.appendChild(heading);
    } else if (block.type === 'text') {
      const textDiv = document.createElement('div');
      textDiv.className = 'block-text';
      textDiv.innerHTML = block.content || '';
      container.appendChild(textDiv);
    } else if (block.type === 'visualization') {
      // Create a visualization object compatible with renderVisualizations
      const viz: any = {
        description: block.description || ''
      };
      if (block.table_data) {
        viz.table_data = block.table_data;
      }
      if (block.chart_html) {
        viz.chart_html = block.chart_html;
      }

      // Render into a wrapper div
      const vizWrapper = document.createElement('div');
      vizWrapper.className = 'block-visualization';
      renderVisualizations([viz], vizWrapper);
      container.appendChild(vizWrapper);
    }
  });

  console.log('[REPORT_RENDERER] renderBlocks - завершение выполнения');
};

// Рендеринг аналитической заметки
export const renderAnalyticalNote = (analyticalNote: string, container: HTMLElement) => {
  if (!analyticalNote) {
    return;
  }
  
  console.log('[REPORT_RENDERER] renderAnalyticalNote - начало выполнения');
  console.log('[REPORT_RENDERER] renderAnalyticalNote - текст для рендеринга:', analyticalNote.substring(0, 100) + '...');
  
  // Проверяем, существует ли уже секция для содержимого
  let section = container.querySelector('.analytical-note-section') as HTMLElement;
  
  if (!section) {
    // Create the section element
    section = document.createElement('section');
    section.className = 'report-section analytical-note-section';
    container.appendChild(section);
    console.log('[REPORT_RENDERER] renderAnalyticalNote - создана новая секция');
  }
  
  // Очищаем секцию перед добавлением нового содержимого
  section.innerHTML = '';
  
  // Создаем div для содержимого
  const contentDiv = document.createElement('div');
  contentDiv.className = 'analytical-note-content';
  
  // Устанавливаем HTML-содержимое
  // Проверяем, содержит ли текст HTML-теги
  if (analyticalNote.includes('<') && analyticalNote.includes('>')) {
    // Если содержит HTML, используем как есть
    contentDiv.innerHTML = analyticalNote;
  } else {
    // Если это простой текст, преобразуем переносы строк в <br>
    contentDiv.innerHTML = analyticalNote.replace(/\n/g, '<br>');
  }
  
  // Добавляем div в секцию
  section.appendChild(contentDiv);
  
  console.log('[REPORT_RENDERER] renderAnalyticalNote - HTML добавлен в контейнер');
  console.log('[REPORT_RENDERER] renderAnalyticalNote - завершение выполнения');
};

// Рендеринг визуализаций
export const renderVisualizations = (visualizations: any[], container: HTMLElement) => {
  if (!visualizations || !Array.isArray(visualizations) || visualizations.length === 0) {
    return;
  }

  // Присваиваем ID контейнеру, если его нет (нужен для DOM-операций)
  if (!container.id) {
    container.id = `container-${Math.random().toString(36).substring(2, 9)}`;
  }

  // Проверяем, существует ли уже секция для визуализаций
  let visualizationsContainer = container.querySelector('.visualizations-container') as HTMLElement;

  if (!visualizationsContainer) {
    visualizationsContainer = document.createElement('div');
    visualizationsContainer.className = 'visualizations-container';
    container.appendChild(visualizationsContainer);
  }

  // Рендерим все визуализации
  visualizations.forEach((viz, index) => {
    
    const section = document.createElement('section');
    section.className = 'report-section visualization-section';
    section.dataset.vizIndex = String(index);
    
    // Устанавливаем безопасные идентификаторы для секции
    if (viz.description) {
      setSafeElementIdentifiers(section, viz.description, index);
    }
    
    // Добавляем описание, если оно есть
    if (viz.description) {
      const descDiv = document.createElement('div');
      descDiv.className = 'viz-description';
      // Обрабатываем текст для добавления пробелов, если их нет
      const processedDescription = processText(viz.description);
      descDiv.innerHTML = `<p>${processedDescription}</p>`;
      section.appendChild(descDiv);
    }
    
    // Build table element (appended later, possibly side-by-side with chart)
    let tableEl: HTMLDivElement | null = null;
    if (viz.table_data) {
      tableEl = document.createElement('div');
      tableEl.className = 'table-container';
      const table = document.createElement('table');
      table.className = 'data-table';

      // Добавляем заголовки
      if (viz.table_data.headers && Array.isArray(viz.table_data.headers)) {
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');

        viz.table_data.headers.forEach((header: any) => {
          const th = document.createElement('th');
          th.textContent = typeof header === 'object' && header !== null ? (header.value ?? '') : String(header ?? '');
          if (typeof header === 'object' && header?.classes) th.className = header.classes;
          headerRow.appendChild(th);
        });

        thead.appendChild(headerRow);
        table.appendChild(thead);
      }

      // Добавляем строки
      if (viz.table_data.rows && Array.isArray(viz.table_data.rows)) {
        const tbody = document.createElement('tbody');

        viz.table_data.rows.forEach((row: any) => {
          const tr = document.createElement('tr');

          // Обрабатываем значения в строке
          if (row.values && Array.isArray(row.values)) {
            row.values.forEach((cell: any) => {
              const td = document.createElement('td');
              if (typeof cell === 'object' && cell !== null) {
                td.textContent = stripHtmlTags(cell.value || '');
                if (cell.classes) {
                  td.className = cell.classes;
                }
              } else {
                td.textContent = stripHtmlTags(String(cell));
              }
              tr.appendChild(td);
            });
          } else if (Array.isArray(row)) {
            row.forEach((cell: any) => {
              const td = document.createElement('td');
              if (typeof cell === 'object' && cell !== null) {
                td.textContent = stripHtmlTags(cell.value || '');
                if (cell.classes) {
                  td.className = cell.classes;
                }
              } else {
                td.textContent = stripHtmlTags(String(cell));
              }
              tr.appendChild(td);
            });
          }

          tbody.appendChild(tr);
        });

        table.appendChild(tbody);
      }

      tableEl.appendChild(table);
    }
    
    // Build chart element (appended later, possibly side-by-side with table)
    let chartEl: HTMLDivElement | null = null;
    // Only create chart element if chart_html contains actual chart content (canvas or script)
    // Skip empty divs like <div style='overflow-x:auto'></div> that LLM sometimes generates
    if (viz.chart_html && (viz.chart_html.includes('<canvas') || viz.chart_html.includes('<script'))) {
      console.log(`[REPORT_RENDERER] renderVisualizations - добавление графика для визуализации #${index}`);
      console.log(`[REPORT_RENDERER] renderVisualizations - chart_html:`,
        viz.chart_html.length > 100 ? viz.chart_html.substring(0, 100) + '...' : viz.chart_html);

      // Проверяем наличие скрипта в HTML
      const hasScript = viz.chart_html.includes('<script>') && viz.chart_html.includes('</script>');
      console.log(`[REPORT_RENDERER] renderVisualizations - HTML содержит скрипт: ${hasScript}`);

      // Предварительная обработка HTML графика для исправления ID с контекстным префиксом "view"
      const processedChartHtml = preprocessChartHtml(viz.chart_html, 'view');
      console.log(`[REPORT_RENDERER] renderVisualizations - HTML после предварительной обработки:`,
        processedChartHtml.length > 100 ? processedChartHtml.substring(0, 100) + '...' : processedChartHtml);

      chartEl = document.createElement('div');
      chartEl.className = 'chart-wrapper';
      const chartDiv = chartEl;
      
      // Сохраняем оригинальный HTML для логирования
      const originalChartHtml = viz.chart_html;
      
      // Ищем ID в оригинальном HTML
      const canvasIdMatch = originalChartHtml.match(/<canvas id=['"]([^'"]+)['"]/);
      const scriptIdMatch = originalChartHtml.match(/getElementById\(['"]([^'"]+)['"]\)/);
      
      const originalCanvasId = canvasIdMatch ? canvasIdMatch[1] : null;
      const originalScriptId = scriptIdMatch ? scriptIdMatch[1] : null;
      
      console.log(`[REPORT_RENDERER] renderVisualizations - оригинальный ID canvas: "${originalCanvasId}", ID в скрипте: "${originalScriptId}"`);
      
      // Проверяем на несоответствие ID
      if (originalCanvasId && originalScriptId && originalCanvasId !== originalScriptId) {
        console.warn(`[REPORT_RENDERER] renderVisualizations - несоответствие ID: canvas="${originalCanvasId}", script="${originalScriptId}"`);
      }
      
      // Извлекаем конфигурацию графика из HTML
      let chartConfig = extractChartConfig(processedChartHtml);
      console.log(`[REPORT_RENDERER] renderVisualizations - извлеченная конфигурация графика:`, chartConfig);

      // Фоллбэк: если extractChartConfig вернул null, пробуем извлечь конфиг через regex
      if (!chartConfig && viz.chart_html) {
        try {
          console.warn('[REPORT_RENDERER] renderVisualizations - extractChartConfig вернул null, пробуем regex-фоллбэк');
          const chartTypeMatch = viz.chart_html.match(/type:\s*['"](\w+)['"]/);
          const chartType = chartTypeMatch ? chartTypeMatch[1] : 'bar';
          const titleMatch = viz.chart_html.match(/text:\s*['"]([^'"]+)['"]/);
          const title = titleMatch ? titleMatch[1] : viz.description || 'График';

          // Извлекаем labels
          const labelsMatch = viz.chart_html.match(/labels:\s*(\[[^\]]*\])/);
          let labels: string[] = [];
          if (labelsMatch) {
            try { labels = JSON.parse(labelsMatch[1].replace(/'/g, '"')); } catch (_e) { /* ignore */ }
          }

          // Извлекаем datasets data (первый массив data после datasets)
          const dataMatch = viz.chart_html.match(/datasets:\s*\[\s*\{[^}]*data:\s*(\[[^\]]*\])/);
          let data: number[] = [];
          if (dataMatch) {
            try { data = JSON.parse(dataMatch[1]); } catch (_e) { /* ignore */ }
          }

          if (labels.length > 0 && data.length > 0) {
            chartConfig = {
              type: chartType,
              labels: labels,
              datasets: [{
                label: title,
                data: data,
                backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(255, 206, 86, 0.7)',
                  'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)']
              }],
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { title: { display: true, text: title } }
              }
            };
            console.log('[REPORT_RENDERER] renderVisualizations - regex-фоллбэк: конфиг создан', chartConfig);
          }
        } catch (fallbackError) {
          console.error('[REPORT_RENDERER] renderVisualizations - regex-фоллбэк ошибка:', fallbackError);
        }
      }

      // Устанавливаем обработанный HTML
      chartDiv.innerHTML = processedChartHtml;

      // Находим canvas элемент и устанавливаем атрибут data-chart
      const canvasElementsForDataAttr = chartDiv.querySelectorAll('canvas');
      if (canvasElementsForDataAttr.length > 0 && chartConfig) {
        console.log(`[REPORT_RENDERER] renderVisualizations - устанавливаем атрибут data-chart для canvas элементов`);
        canvasElementsForDataAttr.forEach((canvas) => {
          canvas.setAttribute('data-chart', JSON.stringify(chartConfig));
          console.log(`[REPORT_RENDERER] renderVisualizations - атрибут data-chart установлен для canvas с ID "${canvas.id}"`);
        });
      }
      
      // Проверяем, что скрипты сохранились после установки innerHTML
      const scriptsAfterInnerHTML = chartDiv.querySelectorAll('script');
      console.log(`[REPORT_RENDERER] renderVisualizations - количество скриптов после innerHTML: ${scriptsAfterInnerHTML.length}`);
      
      // Если скрипты не сохранились, добавляем их вручную
      if (hasScript && scriptsAfterInnerHTML.length === 0) {
        console.warn('[REPORT_RENDERER] renderVisualizations - скрипты были потеряны при установке innerHTML, добавляем вручную');
        
        // Извлекаем содержимое скрипта из оригинального HTML
        const scriptMatch = processedChartHtml.match(/<script>([\s\S]*?)<\/script>/);
        if (scriptMatch && scriptMatch[1]) {
          const scriptContent = scriptMatch[1];
          console.log('[REPORT_RENDERER] renderVisualizations - извлеченное содержимое скрипта:', 
            scriptContent.length > 100 ? scriptContent.substring(0, 100) + '...' : scriptContent);
          
          // Создаем новый элемент script
          const scriptElement = document.createElement('script');
          scriptElement.textContent = scriptContent;
          
          // Добавляем скрипт в DOM
          chartDiv.appendChild(scriptElement);
          console.log('[REPORT_RENDERER] renderVisualizations - скрипт добавлен вручную');
        }
      }
      
      // Создаем уникальный ID для canvas элемента
      const canvasElements = chartDiv.querySelectorAll('canvas');
      console.log(`[REPORT_RENDERER] renderVisualizations - найдено ${canvasElements.length} canvas элементов`);
      
      canvasElements.forEach((canvas, canvasIndex) => {
        const originalId = canvas.id;
        console.log(`[REPORT_RENDERER] renderVisualizations - canvas #${canvasIndex} имеет ID: "${originalId}"`);
        
        // Используем более стабильный ID без использования timestamp
        chartIdCounter++;
        const newId = `chart-${index}-${canvasIndex}-${chartIdCounter}`;
        console.log(`[REPORT_RENDERER] renderVisualizations - новый ID для canvas #${canvasIndex}: "${newId}"`);
        
        // Сохраняем старый ID для логирования
        const oldId = canvas.id;
        canvas.id = newId;
        
        // Обновляем ID в скрипте - используем более надежный подход
        const scripts = chartDiv.querySelectorAll('script');
        console.log(`[REPORT_RENDERER] renderVisualizations - найдено ${scripts.length} скриптов для обновления`);
        
        scripts.forEach((script, scriptIndex) => {
          let scriptContent = script.textContent || '';
            console.log(`[REPORT_RENDERER] renderVisualizations - обработка скрипта #${scriptIndex}, длина: ${scriptContent.length}`);
          
          // Ищем все вхождения getElementById с оригинальным ID
          const idRegex = new RegExp(`getElementById\\(['"]${originalId}['"]\\)`, 'g');
          if (scriptContent && idRegex.test(scriptContent)) {
            console.log(`[REPORT_RENDERER] renderVisualizations - найдено точное совпадение ID "${originalId}" в скрипте`);
            // Заменяем все вхождения
            const updatedContent = scriptContent.replace(idRegex, `getElementById('${newId}')`);
            console.log(`[REPORT_RENDERER] renderVisualizations - обновлен скрипт, заменен ID "${originalId}" на "${newId}"`);
            script.textContent = updatedContent;
          } else {
            // Если точное совпадение не найдено, ищем любой getElementById
            const anyIdMatch = scriptContent.match(/getElementById\(['"]([^'"]+)['"]\)/);
            if (anyIdMatch) {
              const scriptId = anyIdMatch[1];
              console.log(`[REPORT_RENDERER] renderVisualizations - найден другой ID в скрипте: "${scriptId}", не совпадает с ID canvas "${originalId}"`);
              
              // Заменяем ID в скрипте на новый ID canvas
              const updatedContent = scriptContent.replace(
                new RegExp(`getElementById\\(['"]${scriptId}['"]\\)`, 'g'),
                `getElementById('${newId}')`
              );
              console.log(`[REPORT_RENDERER] renderVisualizations - обновлен скрипт, заменен ID "${scriptId}" на "${newId}"`);
              script.textContent = updatedContent;
            } else {
              // Ищем паттерн с переменной: const chartIdN = 'someId'; ... getElementById(chartIdN)
              const varIdMatch = scriptContent.match(/const\s+(\w+)\s*=\s*['"]([^'"]+)['"][\s\S]*getElementById\(\s*\1\s*\)/);
              if (varIdMatch) {
                const varName = varIdMatch[1];
                const varValue = varIdMatch[2];
                const updatedContent = scriptContent.replace(
                  new RegExp(`(const\\s+${varName}\\s*=\\s*)['"]${varValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}['"]`),
                  `$1'${newId}'`
                );
                script.textContent = updatedContent;
              }
            }
          }
        });
      });
      
    }

    // Append chart and table: side-by-side when both exist (chart left, table right)
    if (tableEl && chartEl) {
      const contentRow = document.createElement('div');
      contentRow.className = 'viz-content-row';
      contentRow.appendChild(chartEl);
      contentRow.appendChild(tableEl);
      section.appendChild(contentRow);
    } else {
      if (chartEl) section.appendChild(chartEl);
      if (tableEl) section.appendChild(tableEl);
    }

    visualizationsContainer.appendChild(section);
  });
};

// Функция для обработки текста и добавления пробелов между словами, если их нет
const processText = (text: string): string => {
  // Проверяем, есть ли пробелы в тексте
  if (text.includes(' ')) {
    return text; // Если пробелы есть, возвращаем текст как есть
  }
  
  // Регулярное выражение для добавления пробелов между словами
  // Ищем границу между строчной и заглавной буквой или между буквой и цифрой
  return text
    .replace(/([а-яёa-z])([А-ЯЁA-Z])/g, '$1 $2') // Между строчной и заглавной
    .replace(/([А-ЯЁA-Z])([А-ЯЁA-Z][а-яёa-z])/g, '$1 $2') // Между заглавными, если за второй следует строчная
    .replace(/([а-яёa-zА-ЯЁA-Z])(\d)/g, '$1 $2') // Между буквой и цифрой
    .replace(/(\d)([а-яёa-zА-ЯЁA-Z])/g, '$1 $2') // Между цифрой и буквой
    .replace(/\.([А-ЯЁA-Z])/g, '. $1'); // После точки перед заглавной буквой
};

// Рендеринг секции с общим резюме
export const renderSummarySection = (summary: any, container: HTMLElement) => {
  // Проверяем, что summary.summary существует и не пустой
  const summaryText = typeof summary.summary === 'string' 
    ? summary.summary.trim() 
    : String(summary.summary || '').trim();
    
  if (!summaryText) {
    return; // Не отображаем пустую секцию
  }
  
  // Обрабатываем текст для добавления пробелов, если их нет
  const processedText = processText(summaryText);
  
  const section = document.createElement('section');
  section.className = 'report-section summary-section';

  const summaryContent = document.createElement('div');
  summaryContent.className = 'summary-content';
  summaryContent.innerHTML = `<p>${processedText}</p>`;
  section.appendChild(summaryContent);

  container.appendChild(section);
};

// Рендеринг ключевых выводов
export const renderKeyFindings = (findings: any, container: HTMLElement) => {
  // Ensure findings is an array before using forEach
  const findingsArray = Array.isArray(findings) ? findings : 
                       (typeof findings === 'string' ? [findings] : []);
  
  // Проверяем, что массив не пустой и содержит непустые элементы
  const nonEmptyFindings = findingsArray.filter(finding => {
    const findingText = typeof finding === 'string' ? finding.trim() : String(finding).trim();
    return findingText.length > 0;
  });
  
  if (nonEmptyFindings.length === 0) {
    return; // Не отображаем пустую секцию
  }
  
  const section = document.createElement('section');
  section.className = 'report-section findings-section';
  
  const title = document.createElement('h2');
  title.textContent = 'Ключевые выводы';
  section.appendChild(title);

  const list = document.createElement('ul');
  list.className = 'findings-list';
  
  nonEmptyFindings.forEach(finding => {
    const item = document.createElement('li');
    // Обрабатываем текст для добавления пробелов, если их нет
    const findingText = typeof finding === 'string' ? finding : String(finding);
    item.innerHTML = processText(findingText);
    list.appendChild(item);
  });

  section.appendChild(list);
  container.appendChild(section);
};

// Рендеринг секций отчета
export const renderReportSections = (reports: any[], container: HTMLElement) => {
  reports.forEach((report, index) => {
    const section = document.createElement('section');
    section.className = 'report-section report-analysis-section';
    
    // Removed the title to avoid duplication
    
    // Render analytical note if available
    if (report.actions && report.actions.analytical_note) {
      // Ensure analytical_note is a string before using replace
      const noteText = typeof report.actions.analytical_note === 'string' 
        ? report.actions.analytical_note.replace(/\n/g, '<br>')
        : String(report.actions.analytical_note || '');
      
      // Проверяем, что текст не пустой после удаления пробелов
      const trimmedNoteText = noteText.trim();
      
      if (trimmedNoteText.length > 0) {
        const noteDiv = document.createElement('div');
        noteDiv.className = 'analytical-note';
        // Обрабатываем текст для добавления пробелов, если их нет
        const processedText = processText(trimmedNoteText);
        noteDiv.innerHTML = `<p>${processedText}</p>`;
        section.appendChild(noteDiv);
      }
    }

    // Render visualizations if available
    if (report.actions && report.actions.visualizations && report.actions.visualizations.length > 0) {
      report.actions.visualizations.forEach((viz: any, vizIndex: number) => {
        const vizDiv = document.createElement('div');
        vizDiv.className = 'visualization-container';
        
        // Add description if available
        if (viz.description) {
          const descDiv = document.createElement('div');
          descDiv.className = 'viz-description';
          // Обрабатываем текст для добавления пробелов, если их нет
          const processedDescription = processText(viz.description);
          descDiv.innerHTML = `<p>${processedDescription}</p>`;
          vizDiv.appendChild(descDiv);
        }
        
        // Add chart canvas
        const chartDiv = document.createElement('div');
        chartDiv.className = 'chart-wrapper';
        const canvas = document.createElement('canvas');
        // Используем более стабильный ID без дополнительных компонентов и timestamp
        chartIdCounter++;
        canvas.id = `chart-${index}-${vizIndex}-${chartIdCounter}`;
        
        // Store chart data as attribute for initialization
        if (viz.chart_html) {
          // Extract chart configuration from chart_html
          const chartConfig = extractChartConfig(viz.chart_html);
          if (chartConfig) {
            console.log(`[REPORT_RENDERER] renderReportSections - извлечена конфигурация графика для визуализации #${vizIndex}:`, chartConfig);
            canvas.setAttribute('data-chart', JSON.stringify(chartConfig));
            console.log(`[REPORT_RENDERER] renderReportSections - атрибут data-chart установлен для canvas с ID "${canvas.id}"`);
          } else {
            console.warn(`[REPORT_RENDERER] renderReportSections - не удалось извлечь конфигурацию графика для визуализации #${vizIndex}`);
            
            // Если не удалось извлечь конфигурацию, создаем базовую конфигурацию на основе типа графика
            try {
              const chartType = viz.chart_html.includes('type: \'pie\'') ? 'pie' : 'bar';
              const titleMatch = viz.chart_html.match(/text: ['"]([^'"]+)['"]/);
              const title = titleMatch ? titleMatch[1] : viz.description || 'График';
              
              // Ищем данные в HTML
              const labelsMatch = viz.chart_html.match(/labels: (\[[^\]]+\])/);
              const dataMatch = viz.chart_html.match(/data: (\[[^\]]+\])/);
              
              const labels = labelsMatch ? JSON.parse(labelsMatch[1].replace(/'/g, '"')) : [];
              const data = dataMatch ? JSON.parse(dataMatch[1]) : [];
              
              const basicConfig = {
                type: chartType,
                labels: labels,
                datasets: [{
                  label: title,
                  data: data,
                  backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)', 'rgba(255, 206, 86, 0.7)']
                }],
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  title: {
                    display: true,
                    text: title
                  }
                }
              };
              
              console.log(`[REPORT_RENDERER] renderReportSections - создана базовая конфигурация графика:`, basicConfig);
              canvas.setAttribute('data-chart', JSON.stringify(basicConfig));
              console.log(`[REPORT_RENDERER] renderReportSections - атрибут data-chart установлен с базовой конфигурацией`);
            } catch (error) {
              console.error(`[REPORT_RENDERER] renderReportSections - ошибка при создании базовой конфигурации:`, error);
            }
          }
        }
        
        chartDiv.appendChild(canvas);

        // Build table element if available
        let tableDiv: HTMLDivElement | null = null;
        if (viz.table_data) {
          tableDiv = document.createElement('div');
          tableDiv.className = 'table-container';
          const table = document.createElement('table');
          table.className = 'data-table';

          // Add headers
          if (viz.table_data.headers) {
            const thead = document.createElement('thead');
            const headerRow = document.createElement('tr');

            viz.table_data.headers.forEach((header: any) => {
              const th = document.createElement('th');
              th.textContent = typeof header === 'object' && header !== null ? (header.value ?? '') : String(header ?? '');
              if (typeof header === 'object' && header?.classes) th.className = header.classes;
              headerRow.appendChild(th);
            });

            thead.appendChild(headerRow);
            table.appendChild(thead);
          }

          // Add rows
          if (viz.table_data.rows) {
            const tbody = document.createElement('tbody');

            viz.table_data.rows.forEach((row: any) => {
              const tr = document.createElement('tr');

              // Handle different row formats based on the screenshot
              if (row.values && Array.isArray(row.values)) {
                row.values.forEach((cell: any) => {
                  const td = document.createElement('td');
                  if (typeof cell === 'object' && cell !== null) {
                    td.textContent = stripHtmlTags(cell.value || '');
                    if (cell.classes) {
                      td.className = cell.classes;
                    }
                  } else {
                    td.textContent = stripHtmlTags(String(cell));
                  }
                  tr.appendChild(td);
                });
              } else if (Array.isArray(row)) {
                row.forEach((cell: any) => {
                  const td = document.createElement('td');
                  if (typeof cell === 'object' && cell !== null) {
                    td.textContent = stripHtmlTags(cell.value || '');
                    if (cell.classes) {
                      td.className = cell.classes;
                    }
                  } else {
                    td.textContent = stripHtmlTags(String(cell));
                  }
                  tr.appendChild(td);
                });
              } else {
                Object.values(row).forEach((cell: any) => {
                  const td = document.createElement('td');
                  if (typeof cell === 'object' && cell !== null) {
                    td.textContent = stripHtmlTags(cell.value || '');
                    if (cell.classes) {
                      td.className = cell.classes;
                    }
                  } else {
                    td.textContent = stripHtmlTags(String(cell));
                  }
                  tr.appendChild(td);
                });
              }

              tbody.appendChild(tr);
            });

            table.appendChild(tbody);
          }

          tableDiv.appendChild(table);
        }

        // Append chart and table: side-by-side when both exist (chart left, table right)
        if (tableDiv) {
          const contentRow = document.createElement('div');
          contentRow.className = 'viz-content-row';
          contentRow.appendChild(chartDiv);
          contentRow.appendChild(tableDiv);
          vizDiv.appendChild(contentRow);
        } else {
          vizDiv.appendChild(chartDiv);
        }

        section.appendChild(vizDiv);
      });
    }

    container.appendChild(section);
  });
};

// Рендеринг рекомендаций
export const renderRecommendations = (recommendations: any, container: HTMLElement) => {
  // Ensure recommendations is an array before using forEach
  const recommendationsArray = Array.isArray(recommendations) ? recommendations : 
                              (typeof recommendations === 'string' ? [recommendations] : []);
  
  // Проверяем, что массив не пустой и содержит непустые элементы
  const nonEmptyRecommendations = recommendationsArray.filter(recommendation => {
    const recommendationText = typeof recommendation === 'string' ? recommendation.trim() : String(recommendation).trim();
    return recommendationText.length > 0;
  });
  
  if (nonEmptyRecommendations.length === 0) {
    return; // Не отображаем пустую секцию
  }
  
  const section = document.createElement('section');
  section.className = 'report-section recommendations-section';
  
  const title = document.createElement('h2');
  title.textContent = 'Рекомендации';
  section.appendChild(title);

  const list = document.createElement('ul');
  list.className = 'recommendations-list';
  
  nonEmptyRecommendations.forEach(recommendation => {
    const item = document.createElement('li');
    // Обрабатываем текст для добавления пробелов, если их нет
    const recommendationText = typeof recommendation === 'string' ? recommendation : String(recommendation);
    item.innerHTML = processText(recommendationText);
    list.appendChild(item);
  });

  section.appendChild(list);
  container.appendChild(section);
};

// Вспомогательные функции для форматирования данных отчета
const createTableData = (data: any[]): { headers: string[], rows: any[][] } | null => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  
  const headers = Object.keys(data[0]);
  const rows = data.map(item => headers.map(header => item[header]));
  
  return { headers, rows };
};

const createBulletList = (items: any): string => {
  if (!items || !Array.isArray(items) || items.length === 0) return '';
  return items.map(item => `<li>${typeof item === 'string' ? item : String(item)}</li>`).join('');
};

const getLabelsAndValues = (data: any[], labelKey?: string, valueKey?: string): { labels: any[], values: any[] } => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { labels: [], values: [] };
  }
  
  // Try to find appropriate keys for labels and values
  const findKey = (obj: any, possibleKeys: string[]): string | undefined => {
    return possibleKeys.find(key => obj[key] !== undefined);
  };
  
  const labelKeys = labelKey ? [labelKey] : ['label', 'name', 'company', 'category', 'type'];
  const valueKeys = valueKey ? [valueKey] : ['value', 'percentage', 'amount', 'total', 'count'];
  
  const actualLabelKey = findKey(data[0], labelKeys) || Object.keys(data[0])[0];
  const actualValueKey = findKey(data[0], valueKeys) || 
                         Object.keys(data[0]).find(key => typeof data[0][key] === 'number') || 
                         Object.keys(data[0])[1];
  
  return {
    labels: data.map(item => item[actualLabelKey] || ''),
    values: data.map(item => item[actualValueKey] || 0)
  };
};

// Export these functions to fix the ESLint warnings
export const extractSections = (report: any): any[] => {
  const visualizations: any[] = [];
  
  // Process sections if available
  if (report.sections && Array.isArray(report.sections)) {
    report.sections.forEach((section: any) => {
      // Add section title and content
      if (section.title || section.content) {
        visualizations.push({
          description: section.title || 'Раздел',
          chart_html: `<div class="section-content">
            ${section.title ? `<h3>${section.title}</h3>` : ''}
            ${section.content ? `<p>${typeof section.content === 'string' ? section.content.replace(/\n/g, '<br>') : String(section.content)}</p>` : ''}
          </div>`,
          table_data: null
        });
      }
      
      // Add section data as table if it's an array
      if (section.data && Array.isArray(section.data) && section.data.length > 0) {
        const tableData = createTableData(section.data);
        if (tableData) {
          visualizations.push({
            description: section.title ? `Данные: ${section.title}` : 'Таблица данных',
            chart_html: '',
            table_data: tableData
          });
        }
      }
      
      // Add bullet points if available
      if (section.bullet_points && Array.isArray(section.bullet_points) && section.bullet_points.length > 0) {
        visualizations.push({
          description: section.title || 'Ключевые моменты',
          chart_html: `<div class="bullet-points"><ul>${createBulletList(section.bullet_points)}</ul></div>`,
          table_data: null
        });
      }
      
      // Add section table if available
      if (section.table && Array.isArray(section.table) && section.table.length > 0) {
        const tableData = createTableData(section.table);
        if (tableData) {
          visualizations.push({
            description: section.title || 'Таблица данных',
            chart_html: '',
            table_data: tableData
          });
        }
      }
      
      // Process embedded visualization if available
      if (section.visualization) {
        const viz = section.visualization;
        
        try {
          if (!viz.data || !viz.type) {
            console.error('Отсутствуют необходимые данные для визуализации:', viz);
            return;
          }
          
          const { labels, values } = getLabelsAndValues(
            Array.isArray(viz.data) ? viz.data : [],
            undefined,
            undefined
          );
          
          const chartType = viz.type.includes('pie') ? 'pie' : 'bar';
          const title = viz.title || section.title || (chartType === 'pie' ? 'Круговая диаграмма' : 'Столбчатая диаграмма');
          
          visualizations.push({
            description: title,
            chart_html: createChartHtml(chartType, title, labels, values, 'gen'),
            table_data: null
          });
        } catch (error) {
          console.error('Ошибка при обработке встроенной визуализации:', error, viz);
        }
      }
    });
  }
  
  return visualizations;
};

export const extractCharts = (report: any): any[] => {
  const visualizations: any[] = [];
  
  // Process charts if available
  if (report.charts && Array.isArray(report.charts)) {
    report.charts.forEach((chart: any) => {
      try {
        if (!chart.data || !chart.type || !chart.title) {
          console.error('Отсутствуют необходимые данные для графика:', chart);
          return;
        }
        
        const { labels, values } = getLabelsAndValues(
          Array.isArray(chart.data) ? chart.data : [],
          undefined,
          undefined
        );
        
        const chartType = chart.type.includes('pie') ? 'pie' : 'bar';
        
        visualizations.push({
          description: chart.title || (chartType === 'pie' ? 'Круговая диаграмма' : 'Столбчатая диаграмма'),
          chart_html: createChartHtml(chartType, chart.title, labels, values, 'gen'),
          table_data: null
        });
      } catch (error) {
        console.error('Ошибка при обработке графика:', error, chart);
      }
    });
  }
  
  return visualizations;
};

export const extractVisualizations = (report: any): any[] => {
  const visualizations: any[] = [];
  
  // Process visualizations if available
  if (report.visualizations && Array.isArray(report.visualizations)) {
    report.visualizations.forEach((viz: any) => {
      try {
        if (!viz.data) {
          console.error('Отсутствуют данные для визуализации:', viz);
          return;
        }
        
        const labels = viz.data.categories || viz.data.labels || [];
        const values = viz.data.values || [];
        
        const chartType = viz.type === 'pie_chart' ? 'pie' : 'bar';
        
        visualizations.push({
          description: viz.title || (chartType === 'pie' ? 'Круговая диаграмма' : 'Столбчатая диаграмма'),
          chart_html: createChartHtml(chartType, viz.title || '', labels, values, 'gen'),
          table_data: null
        });
      } catch (error) {
        console.error('Ошибка при обработке визуализации:', error, viz);
      }
    });
  }
  
  return visualizations;
};

export const extractVisualizationSuggestions = (report: any, suggestions: any[]): any[] => {
  if (!suggestions || !Array.isArray(suggestions) || !report || !report.data) {
    return [];
  }
  
  return suggestions.map((suggestion: any) => {
    const visualization: any = {
      description: suggestion.description || '',
      chart_html: '',
      table_data: null
    };
    
    // Create table data
    if (report.data && report.data.length > 0) {
      const tableData = createTableData(report.data);
      if (tableData) {
        visualization.table_data = tableData;
      }
      
      // Create chart HTML
      const { labels, values } = getLabelsAndValues(
        report.data,
        undefined,
        suggestion.type === 'pie_chart' ? 'percentage' : 'total_premium'
      );
      
      const chartType = suggestion.type === 'pie_chart' ? 'pie' : 'bar';
      visualization.chart_html = createChartHtml(chartType, suggestion.title || '', labels, values, 'gen');
    }
    
    return visualization;
  });
};

export const extractDataInsights = (insights: any): any[] => {
  const visualizations: any[] = [];
  
  if (!insights) return visualizations;
  
  // Создаем визуализации на основе данных insights
  // Преобразуем объект insights в массив пар ключ-значение
  const insightEntries = Object.entries(insights);
  
  // Если есть какие-то статистические данные, создаем HTML-список
  const statEntries = insightEntries.filter(([key, value]) => 
    typeof value === 'number' || 
    (typeof value === 'object' && value !== null && !Array.isArray(value))
  );
  
  if (statEntries.length > 0) {
    let statsHtml = '<div class="data-insights">';
    statsHtml += '<h4>Статистика</h4>';
    statsHtml += '<ul>';
    
    statEntries.forEach(([key, value]) => {
      if (typeof value === 'number') {
        // Форматируем числовые значения
        statsHtml += `<li>${key}: ${value.toLocaleString()}</li>`;
      } else if (typeof value === 'object' && value !== null) {
        // Обрабатываем объекты (например, min_value, max_value и т.д.)
        const objEntries = Object.entries(value);
        const formattedValues = objEntries
          .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toLocaleString() : v}`)
          .join(', ');
        
        statsHtml += `<li>${key}: ${formattedValues}</li>`;
      }
    });
    
    statsHtml += '</ul></div>';
    
    visualizations.push({
      description: 'Статистика',
      chart_html: statsHtml,
      table_data: null
    });
  }
  
  // Обрабатываем массивы данных для создания графиков и таблиц
  insightEntries.forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      // Создаем таблицу, если это массив объектов
      if (typeof value[0] === 'object' && value[0] !== null) {
        const tableData = createTableData(value);
        if (tableData) {
          visualizations.push({
            description: key,
            chart_html: '',
            table_data: tableData
          });
        }
        
        // Если в объектах есть числовые значения, создаем график
        const { labels, values } = getLabelsAndValues(value);
        if (labels.length > 0 && values.length > 0) {
          // Определяем тип графика на основе данных
          // Если меньше 5 элементов, используем круговую диаграмму, иначе столбчатую
          const chartType = value.length <= 5 ? 'pie' : 'bar';
          
          visualizations.push({
            description: `${key} (график)`,
            chart_html: createChartHtml(chartType, key, labels, values, 'gen'),
            table_data: null
          });
        }
      }
    }
  });
  
  return visualizations;
};

// Функция для предварительной обработки HTML графика
export const preprocessChartHtml = (chartHtml: string, contextPrefix: string = ''): string => {
  console.log('[REPORT_RENDERER] preprocessChartHtml - начало обработки HTML графика');
  console.log('[REPORT_RENDERER] preprocessChartHtml - исходный HTML:', 
    chartHtml.length > 100 ? chartHtml.substring(0, 100) + '...' : chartHtml);
  console.log('[REPORT_RENDERER] preprocessChartHtml - contextPrefix:', contextPrefix);
  
  try {
    // Генерируем уникальный ID для графика с учетом контекстного префикса
    chartIdCounter++;
    const uniqueId = contextPrefix ? `${contextPrefix}-chart-preprocessed-${chartIdCounter}` : `chart-preprocessed-${chartIdCounter}`;
    console.log(`[REPORT_RENDERER] preprocessChartHtml - сгенерирован новый ID: "${uniqueId}"`);
    
    // Ищем ID в canvas
    const canvasIdMatch = chartHtml.match(/<canvas id=['"]([^'"]+)['"]/);
    const originalCanvasId = canvasIdMatch ? canvasIdMatch[1] : null;
    
    // Ищем ID в скрипте
    const scriptIdMatch = chartHtml.match(/getElementById\(['"]([^'"]+)['"]\)/);
    const originalScriptId = scriptIdMatch ? scriptIdMatch[1] : null;
    
    console.log(`[REPORT_RENDERER] preprocessChartHtml - найденные ID: canvas="${originalCanvasId}", script="${originalScriptId}"`);
    
    // Если ID не найдены, возвращаем исходный HTML
    if (!originalCanvasId && !originalScriptId) {
      console.warn('[REPORT_RENDERER] preprocessChartHtml - ID не найдены в HTML');
      return chartHtml;
    }
    
    // Заменяем ID в canvas
    let processedHtml = chartHtml;
    if (originalCanvasId) {
      processedHtml = processedHtml.replace(
        new RegExp(`<canvas id=['"]${originalCanvasId}['"]`, 'g'),
        `<canvas id="${uniqueId}"`
      );
      console.log(`[REPORT_RENDERER] preprocessChartHtml - заменен ID в canvas: "${originalCanvasId}" -> "${uniqueId}"`);
    }
    
    // Заменяем ID в скрипте
    if (originalScriptId) {
      processedHtml = processedHtml.replace(
        new RegExp(`getElementById\\(['"]${originalScriptId}['"]\\)`, 'g'),
        `getElementById('${uniqueId}')`
      );
      console.log(`[REPORT_RENDERER] preprocessChartHtml - заменен ID в скрипте: "${originalScriptId}" -> "${uniqueId}"`);
    } else if (originalCanvasId) {
      // Ищем паттерн с переменной: const chartIdN = 'chart_viz1'; ... getElementById(chartIdN)
      const varIdMatch = processedHtml.match(new RegExp(`const\\s+(\\w+)\\s*=\\s*['"]${originalCanvasId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}['"]`));
      if (varIdMatch) {
        const varName = varIdMatch[1];
        processedHtml = processedHtml.replace(
          new RegExp(`(const\\s+${varName}\\s*=\\s*)['"]${originalCanvasId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}['"]`),
          `$1'${uniqueId}'`
        );
      }
    }
    return processedHtml;
  } catch (error) {
    console.error('[REPORT_RENDERER] preprocessChartHtml - ошибка при обработке HTML:', error);
    // В случае ошибки возвращаем исходный HTML
    return chartHtml;
  }
};

// Функция для преобразования данных отчета в стандартный формат
export const formatReportData = (reportResult: any) => {
  console.log("formatReportData вызван с данными:", reportResult);
  
  // Если данные уже в нужном формате с reports, возвращаем как есть, сохраняя summary
  if (reportResult.reports) {
    return reportResult;
  }

  // Handle blocks format (new block-based format from LLM)
  if (reportResult.blocks && Array.isArray(reportResult.blocks)) {
    console.log("formatReportData - обнаружен формат blocks, количество:", reportResult.blocks.length);
    return reportResult; // Pass through, renderReportContent handles blocks directly
  }
  
  // Обработка partial data формата (когда данные приходят с warning, partial_data, analytical_note)
  if (reportResult.partial_data && reportResult.analytical_note) {
    console.log("formatReportData - обнаружен partial data формат");

    // Check if analytical_note is actually a stringified JSON with blocks
    if (typeof reportResult.analytical_note === 'string' && reportResult.analytical_note.includes('"blocks"')) {
      // Try full JSON.parse first, then fall back to partial extraction
      try {
        const parsed = JSON.parse(reportResult.analytical_note);
        if (parsed.blocks && Array.isArray(parsed.blocks)) {
          console.log("formatReportData - analytical_note содержит полный blocks JSON");
          return parsed;
        }
      } catch (e) {
        // JSON is incomplete/truncated, use partial extraction
        console.log("formatReportData - analytical_note содержит неполный blocks JSON, извлекаем частично");
        const extracted = extractPartialData(reportResult.analytical_note);
        if (extracted && extracted.blocks && extracted.blocks.length > 0) {
          console.log("formatReportData - извлечено блоков:", extracted.blocks.length);
          return extracted;
        }
      }
    }

    return {
      reports: [{
        name: reportResult.report_name || "Отчет",
        index: reportResult.report_index || 0,
        actions: {
          analytical_note: reportResult.analytical_note,
          visualizations: reportResult.visualizations || []
        }
      }],
      meta: {
        status: "partial",
        warning: reportResult.warning
      }
    };
  }
  
  // Если данные в новом формате с visualizations и analytical_note, преобразуем их в стандартный формат
  if (reportResult.visualizations && reportResult.analytical_note) {
    return {
      reports: [{
        name: reportResult.report_name || "Отчет",
        index: reportResult.report_index || 0,
        actions: {
          analytical_note: reportResult.analytical_note,
          visualizations: reportResult.visualizations
        }
      }],
      meta: {
        status: "success"
      }
    };
  }
  
  // Обработка случая, когда есть только analytical_note без visualizations
  if (reportResult.analytical_note) {
    // Check if analytical_note is actually a stringified JSON with blocks
    if (typeof reportResult.analytical_note === 'string' && reportResult.analytical_note.includes('"blocks"')) {
      try {
        const parsed = JSON.parse(reportResult.analytical_note);
        if (parsed.blocks && Array.isArray(parsed.blocks)) {
          console.log("formatReportData - analytical_note содержит полный blocks JSON");
          return parsed;
        }
      } catch (e) {
        console.log("formatReportData - analytical_note содержит неполный blocks JSON, извлекаем частично");
        const extracted = extractPartialData(reportResult.analytical_note);
        if (extracted && extracted.blocks && extracted.blocks.length > 0) {
          console.log("formatReportData - извлечено блоков:", extracted.blocks.length);
          return extracted;
        }
      }
    }
    console.log("formatReportData - обнаружен формат с только analytical_note");
    return {
      reports: [{
        name: reportResult.report_name || "Отчет",
        index: reportResult.report_index || 0,
        actions: {
          analytical_note: reportResult.analytical_note,
          visualizations: []
        }
      }],
      meta: {
        status: reportResult.partial_data ? "partial" : "success",
        warning: reportResult.warning
      }
    };
  }
  
  // Если данные не в нужном формате, возвращаем пустой объект с reports
  console.warn("Данные не в ожидаемом формате:", reportResult);
  return {
    reports: [],
    meta: {
      status: "error",
      message: "Неверный формат данных"
    }
  };
};
